/** @format */

import { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from 'src/components';
import RoleGuard from 'src/components/RoleGuard';
import { CONTENT_LANGUAGES, ROLES } from 'src/constants';
import { exportLocalesToExcel } from 'src/utils/exportLocalesToExcel';
import {
  collectAllLocaleKeys,
  loadAllLocales,
  sortLocaleCodes,
  type FlatLocalesByCode
} from 'src/utils/flattenLocale';

const ALLOWED_LOCALE_CODES = new Set(CONTENT_LANGUAGES.map(({ value }) => value.toLowerCase()));

function buildEditableFlatLocales(
  flatLocales: FlatLocalesByCode,
  localeCodes: string[],
  keys: string[]
): FlatLocalesByCode {
  const editable: FlatLocalesByCode = {};

  localeCodes.forEach(locale => {
    editable[locale] = {};
    keys.forEach(key => {
      editable[locale][key] = flatLocales[locale]?.[key] ?? '';
    });
  });

  return editable;
}

export default function TranslationsExport() {
  const initialFlatLocales = useMemo(() => {
    const { flatLocales } = loadAllLocales();
    return Object.fromEntries(
      Object.entries(flatLocales).filter(([code]) => ALLOWED_LOCALE_CODES.has(code.toLowerCase()))
    );
  }, []);
  const localeCodes = useMemo(() => sortLocaleCodes(Object.keys(initialFlatLocales)), [initialFlatLocales]);
  const keys = useMemo(() => collectAllLocaleKeys(initialFlatLocales), [initialFlatLocales]);
  const [flatLocales, setFlatLocales] = useState(() =>
    buildEditableFlatLocales(initialFlatLocales, localeCodes, keys)
  );
  const [exporting, setExporting] = useState(false);

  const handleCellChange = useCallback((key: string, locale: string, value: string) => {
    setFlatLocales(prev => ({
      ...prev,
      [locale]: {
        ...prev[locale],
        [key]: value
      }
    }));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportLocalesToExcel(flatLocales);
    } finally {
      setExporting(false);
    }
  };

  return (
    <RoleGuard role={ROLES.ADMIN}>
      <section className="translations-export container">
        <h1 className="content-title">Export Translations</h1>
        <p className="translations-export__summary">
          {localeCodes.length} locales, {keys.length} keys
        </p>
        <div className="translations-export__grid-wrap">
          <table className="translations-export__grid">
            <thead>
              <tr>
                <th className="translations-export__cell--corner">key</th>
                {localeCodes.map(locale => (
                  <th key={locale}>{locale}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map(key => (
                <tr key={key}>
                  <th>{key}</th>
                  {localeCodes.map(locale => {
                    const value = flatLocales[locale][key] ?? '';

                    return (
                      <td key={`${key}-${locale}`}>
                        <input
                          disabled
                          type="text"
                          className={clsx('translations-export__input', !value && 'translations-export__input--empty')}
                          value={value}
                          onChange={event => handleCellChange(key, locale, event.target.value)}
                          aria-label={`${key} ${locale}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="translations-export__toolbar">
          <Button label="Export to Excel" onClick={handleExport} disabled={exporting} />
        </div>
      </section>
    </RoleGuard>
  );
}
